import { useEffect, useMemo, useRef, useState } from 'react';

import { DropdownOption } from '@dbt-labs/sourdough';

import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useAdminDevStagingProdEnvs } from '@/hooks/admin/environments';
import { useProjects } from '@/hooks/admin/project';
import { truthy } from '@/util/array';
import { toTitleCase } from '@/util/string';

export type ProjectEnvironmentSelectorProps = {
  isOpen: boolean;
  showEnvironmentSelector?: boolean;
};

export const useProjectEnvironmentSelector = ({
  isOpen,
  showEnvironmentSelector = true,
}: ProjectEnvironmentSelectorProps) => {
  const { accountId } = useDbtCloudAccount();
  const { data, isLoading } = useProjects({ accountId: accountId! });

  const [selectedProjectOption, setSelectedProjectOption] = useState<DropdownOption[]>(
    [],
  );
  const [selectedEnvironmentOption, setSelectedEnvironmentOption] = useState<
    DropdownOption[]
  >([]);
  const hasSetDefaultEnv = useRef(false);

  const selectedProjectId = selectedProjectOption[0]?.value
    ? Number(selectedProjectOption[0].value)
    : undefined;
  const selectedEnvironmentId = selectedEnvironmentOption[0]?.value
    ? Number(selectedEnvironmentOption[0].value)
    : undefined;

  const projectOptions = useMemo(
    () =>
      (data || [])?.map((project) => ({
        label: project.name,
        value: project.id!,
      })) || [],
    [data],
  );

  const { data: adminEnvironments, isLoading: isLoadingEnvironments } =
    useAdminDevStagingProdEnvs({
      accountId,
      projectId: selectedProjectId,
    });

  const environmentOptions = useMemo(() => {
    if (!adminEnvironments) return [];
    const envs = [
      adminEnvironments.production,
      adminEnvironments.staging,
      adminEnvironments.development,
    ].filter(Boolean);
    return envs.filter(truthy).map((env) => ({
      label: `${env.name} (${toTitleCase(env.type || 'Unknown')})`,
      value: env.id!,
    }));
  }, [adminEnvironments]);

  // reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasSetDefaultEnv.current = false;
    }
  }, [isOpen]);

  // reset the selected environment option when the project changes
  useEffect(() => {
    if (selectedProjectId) {
      setSelectedEnvironmentOption([]);
      hasSetDefaultEnv.current = false;
    }
  }, [selectedProjectId]);

  // set default environment if none is selected
  useEffect(() => {
    if (
      isOpen &&
      showEnvironmentSelector &&
      environmentOptions.length > 0 &&
      selectedEnvironmentOption.length === 0 &&
      !hasSetDefaultEnv.current
    ) {
      setSelectedEnvironmentOption([environmentOptions[0]]);
      hasSetDefaultEnv.current = true;
    }
  }, [
    isOpen,
    environmentOptions,
    selectedEnvironmentOption.length,
    showEnvironmentSelector,
  ]);

  const handleProjectChange = (val: readonly DropdownOption[]) => {
    if (val.length === 0) {
      setSelectedProjectOption([]);
      return;
    }
    const lastSelected = val[val.length - 1];
    setSelectedProjectOption([lastSelected]);
  };

  const handleEnvironmentChange = (val: readonly DropdownOption[]) => {
    if (val.length === 0) {
      setSelectedEnvironmentOption([]);
      return;
    }
    const lastSelected = val[val.length - 1];
    setSelectedEnvironmentOption([lastSelected]);
  };

  return {
    selectedProjectId,
    selectedEnvironmentId,
    projectOptions,
    environmentOptions,
    isLoading,
    isLoadingEnvironments,
    selectedProjectOption,
    selectedEnvironmentOption,
    handleProjectChange,
    handleEnvironmentChange,
  };
};
